import type {
  LoginResponse,
  MeResponse,
  MfaChallengeResponse,
  MfaEnrollRequest,
  MfaEnrollResponse,
  MfaFactorType,
} from "@telecurb/shared";

export interface CurrentUser {
  email: string;
}

// The status the two-step login / accept-invite returns before a session exists.
export type LoginStartResult = LoginResponse;
// What the challenge step renders: the issued challenge id/expiry plus which
// factor kind it is, so the step adapts its copy and hides Resend for TOTP (TEL-170).
export type MfaChallenge = MfaChallengeResponse & { factorType: MfaFactorType };
// What enroll returns: the first challenge, plus a one-time QR + secret for TOTP.
export type MfaEnrollResult = MfaEnrollResponse;
export type { MfaEnrollRequest };

export interface InvitePreview {
  email: string;
  pending: boolean;
  displayName: string | null;
}

export class InvalidCredentialsError extends Error {
  constructor() {
    super("invalid_credentials");
    this.name = "InvalidCredentialsError";
  }
}

export class InvalidInvitationError extends Error {
  constructor() {
    super("invalid_invitation");
    this.name = "InvalidInvitationError";
  }
}

export class WeakPasswordError extends Error {
  constructor() {
    super("weak_password");
    this.name = "WeakPasswordError";
  }
}

// The password-reset bridge token is invalid, expired, or already used
// (TEL-206). The user needs an admin to send a fresh link.
export class InvalidResetTokenError extends Error {
  constructor() {
    super("invalid_reset_token");
    this.name = "InvalidResetTokenError";
  }
}

export class NotAuthenticatedError extends Error {
  constructor() {
    super("not_authenticated");
    this.name = "NotAuthenticatedError";
  }
}

// MFA (TEL-112). The pending bridge between the password step and the SMS verify
// step expired (or the cookie is gone) — the user must restart at sign-in.
export class MfaSessionExpiredError extends Error {
  constructor() {
    super("mfa_session_expired");
    this.name = "MfaSessionExpiredError";
  }
}

// Restart was refused because the bridge came from an existing-factor login or
// no longer matches the user's enrollment state.
export class MfaRestartNotAllowedError extends Error {
  constructor() {
    super("mfa_enrollment_not_allowed");
    this.name = "MfaRestartNotAllowedError";
  }
}

// The entered 6-digit code was wrong; the challenge is still valid (retry/resend).
export class InvalidMfaCodeError extends Error {
  constructor() {
    super("invalid_mfa_code");
    this.name = "InvalidMfaCodeError";
  }
}

// The WorkOS challenge expired or was already consumed — resend a fresh code.
export class MfaChallengeExpiredError extends Error {
  constructor() {
    super("mfa_challenge_expired");
    this.name = "MfaChallengeExpiredError";
  }
}

// WorkOS rejected the phone number at enrollment (bad / non-US mobile).
export class InvalidPhoneError extends Error {
  constructor() {
    super("invalid_phone");
    this.name = "InvalidPhoneError";
  }
}

// The session verified at the auth provider (WorkOS) but no matching DB actor
// exists — `/me` returns 401 { error: "missing_actor" }. Distinct from a plain
// signed-out 401: the user is authenticated upstream but unusable here (e.g. a
// dev DB that was dropped/reseeded, or a deactivated/cleared account). Surfaced
// to the user rather than silently bounced to /sign-in (TEL-143).
export class MissingActorError extends Error {
  constructor() {
    super("missing_actor");
    this.name = "MissingActorError";
  }
}

export type AcceptInviteProfilePatch = {
  displayName?: string;
  phone?: string;
  notifySms?: boolean;
};

// Provider-neutral auth port. The WorkOS implementation lives in
// `workos-client.ts`; swapping providers means writing one new file that
// satisfies this interface and binding it in `context.tsx`.
export interface AuthClient {
  // Two-step (TEL-112): a correct password no longer seals a session — it returns
  // the next MFA status. The session is sealed only by verifyMfa.
  login(email: string, password: string): Promise<LoginStartResult>;
  logout(): Promise<void>;
  getInvite(token: string): Promise<InvitePreview>;
  // Resolves on a successful accept; the new user then completes MFA enrollment
  // (the server always responds with mfa_enrollment_required).
  acceptInvite(
    token: string,
    password: string,
    profilePatch?: AcceptInviteProfilePatch,
  ): Promise<void>;
  // TEL-312: self-service forgot-password. Always resolves — never reveals
  // whether the email is registered (enumeration prevention).
  requestPasswordReset(email: string): Promise<void>;
  // Completes an admin-initiated password reset from our /reset-password page
  // (TEL-206). No session results — the user signs in with the new password.
  resetPassword(token: string, password: string): Promise<void>;
  // MFA enrollment + verification (TEL-112, TEL-170). enroll takes the chosen
  // method (SMS needs a phone; TOTP needs nothing) and returns the first
  // challenge — plus a one-time QR + secret for TOTP. resend re-issues a
  // challenge (SMS resend). verify seals the session and resolves.
  enrollMfa(req: MfaEnrollRequest): Promise<MfaEnrollResult>;
  resendMfaChallenge(): Promise<MfaChallengeResponse>;
  restartMfaEnrollment(): Promise<void>;
  // rememberDevice (Phase 2) mints a 14-day trusted-device cookie on success.
  verifyMfa(challengeId: string, code: string, rememberDevice?: boolean): Promise<void>;
  getCurrentUser(): Promise<MeResponse | null>;
}
